import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError, onWriteSucceeded } from '@/api/client';
import { dashboardWriteListener } from '@/lib/write-invalidation';
import { ConfirmProvider } from '@/components/ui/ConfirmDialog';
import { SessionProvider } from '@/app/session';
import { AppRoutes } from '@/app/routes';
import { AppBoundary } from '@/app/AppBoundary';
import { NewVersionBanner } from '@/components/ui/NewVersionBanner';
// Initialises the single i18next instance as a side effect, before anything
// renders. `useTranslation` reads that default instance; no provider needed.
import '@/i18n';
import '@/styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Clinic data changes when a colleague changes it, not on a timer.
      // Refetching on focus is what keeps two receptionists looking at the
      // same calendar in agreement.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a 403 or a 404 repeats the same answer more slowly.
        if (error instanceof ApiError && !error.isRetryable) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // A write is never retried automatically: the request may well have
      // succeeded before the response was lost, and repeating it would take
      // the payment twice.
      retry: false,
    },
  },
});

// A booking or a new patient changes Panou's counts, from whichever screen it
// was made; the dashboard's caches hear it here rather than from each caller.
onWriteSucceeded(dashboardWriteListener(queryClient));

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <AppBoundary>
            {/* Confirmările clinicii, nu cele ale browserului: dialogul are
                severitate, cifre, avertisment și loc de scris un motiv. */}
            <ConfirmProvider>
              <AppRoutes />
              <NewVersionBanner />
            </ConfirmProvider>
          </AppBoundary>
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
